import { supabaseClient } from './supabase';
import { RegistrationFormData } from './validations';
import { ExhibitionType } from '../types';
import { cleanQRCodeData } from './utils';

// 티켓 타입 정의
export type TicketType = '일반' | '올데이';

// 등록 데이터 타입 (등록 ID 포함)
export interface RegistrationData extends RegistrationFormData {
  id?: string;
  registration_id?: string;
  is_entered?: boolean;
  ticket_type?: TicketType | null;
  created_at?: string;
  updated_at?: string;
}

// 통계 데이터 타입
export interface RegistrationStats {
  total_registrations: number;
  total_entries: number;
  today_registrations: number;
  pending_entries: number;
  gender_distribution: {
    male: number;
    female: number;
    other: number;
  };
  age_distribution: {
    teen: number;
    twenties: number;
    thirties: number;
    forties: number;
    fifties: number;
    sixties_plus: number;
  };
  region_distribution: Record<string, number>;
}

// 12자리 짧은 등록번호 생성 함수
function generateShortRegistrationCode(exhibitionType: string): string {
  // 전시회 타입별 접두사 (2자리)
  const prefix = exhibitionType === 'urbanbreak' ? 'UB' : 'TC';
  
  // 현재 날짜 (6자리: YYMMDD)
  const now = new Date();
  const year = now.getFullYear().toString().slice(-2);
  const month = (now.getMonth() + 1).toString().padStart(2, '0');
  const day = now.getDate().toString().padStart(2, '0');
  const dateStr = year + month + day;
  
  // 랜덤 문자열 (4자리: 숫자+대문자)
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let randomStr = '';
  for (let i = 0; i < 4; i++) {
    randomStr += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  
  // 최종 12자리 코드: UB251225A1B2
  return prefix + dateStr + randomStr;
}

// 사전등록 생성
export async function createRegistration(data: any): Promise<RegistrationData> {
  try {
    console.log('사전등록 데이터:', data);
      
    // 12자리 짧은 등록번호 생성
    const shortCode = generateShortRegistrationCode(data.exhibition_type);
    // DB에 저장할 데이터 준비 (email, language 컬럼이 이제 존재함)
    const registrationData = {
      name: data.name,
      phone: data.phone,
      email: data.email,          // 이메일 저장 활성화
      nationality: data.nationality,    // 국적 저장 활성화
      gender: data.gender,
      age_group: data.age_group,
      region: data.region,
      privacy_consent: data.privacy_consent,
      marketing_consent: data.marketing_consent,
      sms_consent: data.sms_consent,
      third_party_consent: data.third_party_consent,
      exhibition_type: data.exhibition_type,
      registration_id: shortCode, // 12자리 짧은 등록 ID
    };
    
    console.log('DB 저장 데이터 (email, nationality 포함):', registrationData);

    const { data: registration, error } = await supabaseClient
      .from('registrations')
      .insert([registrationData])
      .select()
      .single();

    if (error) {
      console.error('Supabase 등록 오류:', error);
      throw error;
    }

    
    
    console.log('등록 ID 생성 (12자리):', shortCode);
    
    // 등록 ID 업데이트
    const { data: updatedRegistration, error: updateError } = await supabaseClient
      .from('registrations')
      .update({ registration_id: shortCode })
      .eq('id', registration.id)
      .select()
      .single();

    if (updateError) {
      console.error('등록 ID 업데이트 오류:', updateError);
      // 등록 ID 업데이트 실패해도 등록은 성공으로 처리
    }

    console.log('등록 성공 (12자리 등록 ID):', registration);
    
    // 최종 등록 데이터 반환
    return {
      ...(updatedRegistration || registration),
      registration_id: shortCode,
    };
  } catch (error) {
    console.error('등록 실패:', error);
    throw error;
  }
}

/**
 * 전체 등록 목록 조회
 */
export async function getRegistrations(): Promise<RegistrationData[]> {
  try {
    const { data, error } = await supabaseClient
      .from('registrations')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`조회 실패: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('조회 에러:', error);
    throw error;
  }
}

/**
 * 등록 통계 조회
 */
export async function getRegistrationStats(): Promise<RegistrationStats> {
  try {
    const { data, error } = await supabaseClient
      .from('registrations')
      .select('*');

    if (error) {
      throw new Error(`통계 조회 실패: ${error.message}`);
    }

    const registrations = data || [];
    
    // 오늘 날짜 계산
    const today = new Date().toISOString().split('T')[0];
    const todayRegistrations = registrations.filter(reg => 
      reg.created_at?.startsWith(today)
    );

    // 성별 분포 계산
    const genderCounts = { male: 0, female: 0, other: 0 };
    registrations.forEach(reg => {
      genderCounts[reg.gender as keyof typeof genderCounts]++;
    });

    // 연령대 분포 계산
    const ageCounts = {
      teen: 0,
      twenties: 0,
      thirties: 0,
      forties: 0,
      fifties: 0,
      sixties_plus: 0
    };
    registrations.forEach(reg => {
      ageCounts[reg.age_group as keyof typeof ageCounts]++;
    });

    // 지역 분포 계산
    const regionCounts: Record<string, number> = {};
    registrations.forEach(reg => {
      regionCounts[reg.region] = (regionCounts[reg.region] || 0) + 1;
    });

    return {
      total_registrations: registrations.length,
      total_entries: registrations.filter(reg => reg.is_entered).length,
      today_registrations: todayRegistrations.length,
      pending_entries: registrations.filter(reg => !reg.is_entered).length,
      gender_distribution: genderCounts,
      age_distribution: ageCounts,
      region_distribution: regionCounts,
    };
  } catch (error) {
    console.error('통계 조회 에러:', error);
    throw error;
  }
}

/**
 * QR 코드 데이터에서 registration_id 추출 (오타 패턴 고려)
 */
function extractRegistrationId(parsedData: any): string | null {
  // 정상적인 키들 먼저 확인
  if (parsedData.registration_id) {
    return parsedData.registration_id;
  } else if (parsedData.id) {
    return parsedData.id;
  } else {
    // 오타가 있는 키들 확인
    const possibleKeys = [
      'regstration_id',      // i 누락
      'reistration_id',      // i 위치 잘못
      'regsstration_id',     // s 중복
      'registraton_id',      // i 누락 (다른 위치)
      'registation_id',      // r 누락
      'regisration_id'       // t 누락
    ];
    
    for (const key of possibleKeys) {
      if (parsedData[key]) {
        console.log(`📋 [extractRegistrationId] 오타 키 감지: "${key}" → 값 추출:`, parsedData[key]);
        return parsedData[key];
      }
    }
  }
  
  return null;
}

/**
 * 등록 ID로 등록 정보 조회 (QR 코드 스캔용)
 */
export async function getRegistrationByQR(registrationCode: string): Promise<RegistrationData | null> {
  try {
    console.log('🔍 [getRegistrationByQR] 등록 코드 스캔 시작:', registrationCode);
    
    let data, error;
    let searchValue: string = registrationCode;
    let cleanedCode: string = registrationCode;
    
    // JSON 형태인지 먼저 확인하고 파싱 (원본 데이터 우선)
    try {
      const parsedData = JSON.parse(registrationCode);
      console.log('📋 [getRegistrationByQR] 원본 JSON 파싱 성공, 데이터:', parsedData);
      
      if (parsedData.type === 'exhibition_registration' || parsedData.type?.includes('exhibition')) {
        const extractedId = extractRegistrationId(parsedData);
        
        if (extractedId) {
          searchValue = extractedId;
          console.log('📋 [getRegistrationByQR] 원본 JSON 파싱 성공, registration_id 추출:', searchValue);
        }
      }
    } catch (parseError) {
      console.log('📋 [getRegistrationByQR] 원본 JSON 파싱 실패, 치환 로직으로 재시도');
      
      // 한글 키보드 모드로 스캔된 경우를 위한 치환 처리
      cleanedCode = cleanQRCodeData(registrationCode);
      console.log('🧹 [getRegistrationByQR] 정리된 코드:', cleanedCode);
      
      // 치환된 데이터로 JSON 파싱 재시도
      try {
        const parsedData = JSON.parse(cleanedCode);
        console.log('📋 [getRegistrationByQR] 치환 후 JSON 파싱 성공, 데이터:', parsedData);
        
        if (parsedData.type === 'exhibition_registration' || parsedData.type?.includes('exhibition')) {
          const extractedId = extractRegistrationId(parsedData);
          
          if (extractedId) {
            searchValue = extractedId;
            console.log('📋 [getRegistrationByQR] 치환 후 JSON 파싱 성공, registration_id 추출:', searchValue);
          }
        }
      } catch (secondParseError) {
        console.log('📋 [getRegistrationByQR] 치환 후에도 JSON 파싱 실패, 단순 문자열로 처리:', cleanedCode);
        searchValue = cleanedCode;
      }
    }
    
    // 최종 검색 값을 대문자로 변환 (UUID 형식에 맞춤)
    const finalSearchValue = searchValue.toUpperCase();
    if (finalSearchValue !== searchValue) {
      console.log('📋 [getRegistrationByQR] 최종 대문자 변환:', searchValue, '→', finalSearchValue);
    }
    searchValue = finalSearchValue;
    
    console.log('📋 [getRegistrationByQR] DB 조회 시작, 검색 값:', searchValue);
    
    // 1차 시도: registration_id 컬럼에서 조회 (컬럼이 존재하는 경우)
    let registrationResult: any;
    try {
      registrationResult = await supabaseClient
        .from('registrations')
        .select('*')
        .eq('registration_id', searchValue)
        .single();
      
      data = registrationResult.data;
      error = registrationResult.error;
    } catch (schemaError: any) {
      console.log('⚠️ [getRegistrationByQR] registration_id 컬럼 접근 실패:', schemaError.message);
      error = schemaError;
    }
    
    // 2차 시도: registration_id 실패 또는 컬럼 오류 시 ID(UUID)로 조회
    if (error && (error.code === 'PGRST116' || error.message?.includes('Could not find') || error.message?.includes('registration_id'))) {
      console.log('🔄 [getRegistrationByQR] registration_id 조회 실패, ID로 재시도');
      console.log('🔄 [getRegistrationByQR] 오류 상세:', error.message);
      
      const idResult = await supabaseClient
        .from('registrations')
        .select('*')
        .eq('id', searchValue)
        .single();
      
      data = idResult.data;
      error = idResult.error;
    }
    
    // 3차 시도: qr_code 컬럼에서 조회 (레거시 호환성)
    if (error && error.code === 'PGRST116') {
      console.log('🔄 [getRegistrationByQR] ID 조회도 실패, qr_code 컬럼으로 재시도');
      
      try {
        const qrResult = await supabaseClient
          .from('registrations')
          .select('*')
          .eq('qr_code', searchValue)
          .single();
        
        data = qrResult.data;
        error = qrResult.error;
      } catch (qrError) {
        console.log('🔄 [getRegistrationByQR] qr_code 컬럼도 존재하지 않음');
      }
    }

    if (error) {
      if (error.code === 'PGRST116') {
        console.log('❌ [getRegistrationByQR] 데이터를 찾을 수 없음');
        return null; // 데이터가 없음
      }
      throw new Error(`조회 실패: ${error.message}`);
    }

    console.log('✅ [getRegistrationByQR] DB에서 조회한 데이터:', data);
    console.log('📧 [getRegistrationByQR] DB의 이메일:', data.email);
    console.log('🌐 [getRegistrationByQR] DB의 언어:', data.language);
    console.log('🎫 [getRegistrationByQR] QR 코드:', data.registration_id);

    // DB에서 조회한 데이터를 그대로 반환 (기본값 설정)
    const finalResult = {
      ...data,
      email: data.email || '', // DB에 없으면 빈 문자열
      language: data.language || 'ko', // DB에 없으면 한국어 기본값
      exhibition_type: data.exhibition_type || 'urbanbreak', // DB에 없으면 기본값
    };
    
    console.log('📤 [getRegistrationByQR] 최종 반환 데이터:', finalResult);
    console.log('📧 [getRegistrationByQR] 최종 이메일:', finalResult.email);
    console.log('🌐 [getRegistrationByQR] 최종 언어:', finalResult.language);
    
    return finalResult;
  } catch (error) {
    console.error('❌ [getRegistrationByQR] QR 조회 에러:', error);
    throw error;
  }
}

/**
 * 입장 처리 (QR 코드 스캔 시)
 */
export async function markAsEntered(registrationCode: string, ticketType: TicketType): Promise<RegistrationData> {
  try {
    console.log('🔧 [markAsEntered] 입장 처리 시작, 입력값:', registrationCode);
    
    // 등록 코드 데이터 파싱하여 등록번호 추출
    let registrationId: string = registrationCode;
    let cleanedCode: string = registrationCode;
    
    // JSON 형태인지 먼저 확인하고 파싱 (원본 데이터 우선)
    try {
      const parsedData = JSON.parse(registrationCode);
      console.log('🔧 [markAsEntered] 원본 JSON 파싱 성공, 데이터:', parsedData);
      
      if (parsedData.type === 'exhibition_registration') {
        const extractedId = extractRegistrationId(parsedData);
        if (extractedId) {
          registrationId = extractedId;
          console.log('🔧 [markAsEntered] 원본 JSON에서 registration_id 추출:', registrationId);
        }
      }
    } catch (parseError) {
      console.log('🔧 [markAsEntered] 원본 JSON 파싱 실패, 치환 로직으로 재시도');
      
      // 한글 키보드 모드로 스캔된 경우를 위한 치환 처리
      cleanedCode = cleanQRCodeData(registrationCode);
      console.log('🧹 [markAsEntered] 정리된 코드:', cleanedCode);
      
      // 치환된 데이터로 JSON 파싱 재시도
      try {
        const parsedData = JSON.parse(cleanedCode);
        console.log('🔧 [markAsEntered] 치환 후 JSON 파싱 성공, 데이터:', parsedData);
        
        if (parsedData.type === 'exhibition_registration') {
          const extractedId = extractRegistrationId(parsedData);
          if (extractedId) {
            registrationId = extractedId;
            console.log('🔧 [markAsEntered] 치환 후 JSON에서 registration_id 추출:', registrationId);
          }
        }
      } catch (secondParseError) {
        console.log('🔧 [markAsEntered] 치환 후에도 JSON 파싱 실패, 단순 문자열로 처리:', cleanedCode);
        registrationId = cleanedCode;
      }
    }
    
    console.log('🔧 [markAsEntered] 최종 등록 ID:', registrationId);

    // 업데이트할 데이터 준비 (ticket_type 컬럼 존재 여부에 따라 조건부)
    const updateData: any = {
      is_entered: true,
      updated_at: new Date().toISOString()
    };

    // ticket_type 컬럼이 존재하는 경우에만 추가 (DB 스키마 호환성)
    try {
      updateData.ticket_type = ticketType;
    } catch (schemaError) {
      console.warn('⚠️ [markAsEntered] ticket_type 컬럼이 존재하지 않음, 스키마 업데이트 필요');
    }

    console.log('🔧 [markAsEntered] 업데이트 데이터:', updateData);

    // 1차 시도: registration_id로 업데이트
    let { data, error } = await supabaseClient
      .from('registrations')
      .update(updateData)
      .eq('registration_id', registrationId)
      .select()
      .single();

    // 2차 시도: registration_id로 실패한 경우 id(UUID)로 재시도
    if (error && (error.code === 'PGRST116' || error.message?.includes('Could not find'))) {
      console.log('🔄 [markAsEntered] registration_id 업데이트 실패, ID로 재시도');
      console.log('🔄 [markAsEntered] 오류 상세:', error.message);
      
      // ticket_type 컬럼 오류인 경우 해당 필드 제거하고 재시도
      if (error.message?.includes('ticket_type')) {
        console.log('⚠️ [markAsEntered] ticket_type 컬럼 오류 감지, 해당 필드 제거하고 재시도');
        const { ticket_type, ...updateDataWithoutTicketType } = updateData;
        
        const idResult = await supabaseClient
          .from('registrations')
          .update(updateDataWithoutTicketType)
          .eq('id', registrationId)
          .select()
          .single();
        
        data = idResult.data;
        error = idResult.error;
      } else {
        const idResult = await supabaseClient
          .from('registrations')
          .update(updateData)
          .eq('id', registrationId)
          .select()
          .single();
        
        data = idResult.data;
        error = idResult.error;
      }
    }

    if (error) {
      throw new Error(`입장 처리 실패: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('입장 처리 에러:', error);
    throw error;
  }
}

/**
 * 전화번호로 등록 정보 조회 (중복 체크) - 전시회별로 체크
 */
export async function getRegistrationByPhone(phone: string, exhibitionType?: ExhibitionType): Promise<RegistrationData | null> {
  try {
    let query = supabaseClient
      .from('registrations')
      .select('*')
      .eq('phone', phone);
    
    // 전시회 타입이 지정된 경우 해당 전시회에서만 검색
    if (exhibitionType) {
      query = query.eq('exhibition_type', exhibitionType);
    }
    
    const { data, error } = await query.single();

    if (error) {
      if (error.code === 'PGRST116') {
        return null; // 데이터가 없음
      }
      throw new Error(`조회 실패: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('전화번호 조회 에러:', error);
    return null;
  }
}

/**
 * 관리자 대시보드용 통계 데이터 조회 (COUNT API 사용으로 전체 데이터 기반)
 */
export async function getDashboardStatistics() {
  try {
    // 전체 등록자 수 (COUNT API 사용)
    const { count: totalCount } = await supabaseClient
      .from('registrations')
      .select('*', { count: 'exact', head: true });

    // 입장 완료자 수 (COUNT API 사용)
    const { count: enteredCount } = await supabaseClient
      .from('registrations')
      .select('*', { count: 'exact', head: true })
      .eq('is_entered', true);

    // 오늘 등록자 수 (SELECT로 조회 - 제한적이지만 최신 데이터만)
    const today = new Date();
    const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());
    const { data: todayData } = await supabaseClient
      .from('registrations')
      .select('*')
      .gte('created_at', todayStart.toISOString());
    const todayRegistrations = todayData?.length || 0;

    // 성별 통계 (COUNT API 사용)
    const { count: maleCount } = await supabaseClient
      .from('registrations')
      .select('*', { count: 'exact', head: true })
      .eq('gender', 'male');

    const { count: femaleCount } = await supabaseClient
      .from('registrations')
      .select('*', { count: 'exact', head: true })
      .eq('gender', 'female');

    const { count: otherGenderCount } = await supabaseClient
      .from('registrations')
      .select('*', { count: 'exact', head: true })
      .eq('gender', 'other');

    // 연령대 통계 (COUNT API 사용)
    const ageGroups = ['teen', 'twenties', 'thirties', 'forties', 'fifties', 'sixties_plus'];
    const ageStats: Record<string, number> = {};
    
    for (const age of ageGroups) {
      const { count } = await supabaseClient
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .eq('age_group', age);
      ageStats[age] = count || 0;
    }

    // 지역 통계 (주요 지역만 COUNT API 사용)
    const regions = ['seoul', 'gyeonggi', 'incheon', 'busan', 'daegu', 'daejeon', 'gwangju', 'ulsan', 'sejong', 'gangwon', 'chungbuk', 'chungnam', 'jeonbuk', 'jeonnam', 'gyeongbuk', 'gyeongnam', 'jeju', 'other'];
    const regionStats: Record<string, number> = {};
    
    for (const region of regions) {
      const { count } = await supabaseClient
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .eq('region', region);
      if (count && count > 0) {
        regionStats[region] = count;
      }
    }

    // 최신 등록자 (SELECT로 제한적 조회)
    const { data: latestData } = await supabaseClient
      .from('registrations')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(10);

    return {
      total_registrations: totalCount || 0,
      total_entries: enteredCount || 0,
      today_registrations: todayRegistrations,
      pending_registrations: (totalCount || 0) - (enteredCount || 0),
      gender_stats: {
        male: maleCount || 0,
        female: femaleCount || 0,
        other: otherGenderCount || 0
      },
      age_group_stats: {
        teen: ageStats.teen || 0,
        twenties: ageStats.twenties || 0,
        thirties: ageStats.thirties || 0,
        forties: ageStats.forties || 0,
        fifties: ageStats.fifties || 0,
        sixties_plus: ageStats.sixties_plus || 0
      },
      region_stats: regionStats,
      latest_registrations: latestData || []
    };
  } catch (error) {
    console.error('통계 조회 에러:', error);
    throw error;
  }
}

/**
 * 일별 통계 데이터 조회 (COUNT API 기반으로 전체 데이터 조회)
 */
export async function getDailyStatistics(startDate?: string, endDate?: string) {
  try {
    console.log('📅 [getDailyStatistics] 일별 통계 조회 시작:', startDate, '~', endDate);
    
    // 기본적으로 최근 30일
    const defaultEndDate = new Date();
    const defaultStartDate = new Date();
    defaultStartDate.setDate(defaultStartDate.getDate() - 30);
    
    const queryStartDate = startDate || defaultStartDate.toISOString().split('T')[0];
    const queryEndDate = endDate || defaultEndDate.toISOString().split('T')[0];
    
    // 날짜 범위 생성
    const dateRange: string[] = [];
    const currentDate = new Date(queryStartDate);
    const endDateObj = new Date(queryEndDate);
    
    while (currentDate <= endDateObj) {
      dateRange.push(currentDate.toISOString().split('T')[0]);
      currentDate.setDate(currentDate.getDate() + 1);
    }

    // 각 날짜별로 COUNT API 사용하여 통계 조회
    const dailyData: Array<{
      date: string;
      total_registrations: number;
      total_entries: number;
      urbanbreak: number;
      toycon: number;
      gender_stats: { male: number; female: number; other: number };
    }> = [];

    for (const date of dateRange) {
      const startTime = `${date}T00:00:00`;
      const endTime = `${date}T23:59:59`;

      // 해당 날짜의 총 등록자 수
      const { count: totalCount } = await supabaseClient
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startTime)
        .lte('created_at', endTime);

      // 데이터가 없는 날짜는 건너뛰기
      if (!totalCount || totalCount === 0) {
        continue;
      }

      // 해당 날짜의 입장 완료자 수
      const { count: enteredCount } = await supabaseClient
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startTime)
        .lte('created_at', endTime)
        .eq('is_entered', true);

      // 전시회별 분포
      const { count: urbanbreakCount } = await supabaseClient
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startTime)
        .lte('created_at', endTime)
        .eq('exhibition_type', 'urbanbreak');

      const { count: toyconCount } = await supabaseClient
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startTime)
        .lte('created_at', endTime)
        .eq('exhibition_type', 'toycon');

      // 성별 분포
      const { count: maleCount } = await supabaseClient
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startTime)
        .lte('created_at', endTime)
        .eq('gender', 'male');

      const { count: femaleCount } = await supabaseClient
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startTime)
        .lte('created_at', endTime)
        .eq('gender', 'female');

      const { count: otherCount } = await supabaseClient
        .from('registrations')
        .select('*', { count: 'exact', head: true })
        .gte('created_at', startTime)
        .lte('created_at', endTime)
        .eq('gender', 'other');

      dailyData.push({
        date,
        total_registrations: totalCount || 0,
        total_entries: enteredCount || 0,
        urbanbreak: urbanbreakCount || 0,
        toycon: toyconCount || 0,
        gender_stats: {
          male: maleCount || 0,
          female: femaleCount || 0,
          other: otherCount || 0
        }
      });
    }

    const result = dailyData.sort((a, b) => a.date.localeCompare(b.date));
    
    console.log('✅ [getDailyStatistics] 일별 통계 조회 완료:', result.length, '일간 데이터');
    return result;
  } catch (error) {
    console.error('❌ [getDailyStatistics] 일별 통계 조회 에러:', error);
    throw error;
  }
}

/**
 * 전시회별 등록자 수 조회 (실제 데이터 표시, 제한 없음)
 */
export async function getExhibitionCounts(): Promise<{ total: number; urbanbreak: number; toycon: number; entered: number }> {
  try {
    console.log('🔍 [getExhibitionCounts] 전시회별 등록자 수 조회 시작');
    
    // 전체 등록자 수
    const { count: totalCount, error: totalError } = await supabaseClient
      .from('registrations')
      .select('*', { count: 'exact', head: true });

    if (totalError) {
      throw new Error(`전체 등록자 수 조회 실패: ${totalError.message}`);
    }

    // 입장 완료자 수
    const { count: enteredCount, error: enteredError } = await supabaseClient
      .from('registrations')
      .select('*', { count: 'exact', head: true })
      .eq('is_entered', true);

    if (enteredError) {
      throw new Error(`입장 완료자 수 조회 실패: ${enteredError.message}`);
    }

    // UrbanBreak 등록자 수 (exhibition_type 컬럼 기반)
    const { count: urbanbreakCount, error: urbanbreakError } = await supabaseClient
      .from('registrations')
      .select('*', { count: 'exact', head: true })
      .eq('exhibition_type', 'urbanbreak');

    if (urbanbreakError) {
      throw new Error(`UrbanBreak 등록자 수 조회 실패: ${urbanbreakError.message}`);
    }

    // ToyConvention 등록자 수 (exhibition_type 컬럼 기반)
    const { count: toyconCount, error: toyconError } = await supabaseClient
      .from('registrations')
      .select('*', { count: 'exact', head: true })
      .eq('exhibition_type', 'toycon');

    if (toyconError) {
      throw new Error(`ToyConvention 등록자 수 조회 실패: ${toyconError.message}`);
    }

    const result = {
      total: totalCount || 0,
      entered: enteredCount || 0,
      urbanbreak: urbanbreakCount || 0,
      toycon: toyconCount || 0
    };

    console.log('✅ [getExhibitionCounts] 조회 완료:', result);
    return result;
  } catch (error) {
    console.error('❌ [getExhibitionCounts] 전시회별 등록자 수 조회 에러:', error);
    throw error;
  }
}

// 등록자 목록 조회 옵션 인터페이스
export interface RegistrationListOptions {
  page?: number;
  pageSize?: number;
  search?: string;
  genderFilter?: string;
  ageGroupFilter?: string;
  regionFilter?: string;
  entryStatusFilter?: 'all' | 'entered' | 'pending';
  exhibitionTypeFilter?: 'all' | 'urbanbreak' | 'toycon'; // 전시회별 필터링 추가
  sortBy?: 'created_at' | 'name' | 'phone';
  sortOrder?: 'asc' | 'desc';
}

// 등록자 목록 응답 인터페이스
export interface RegistrationListResponse {
  data: RegistrationData[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

/**
 * 등록자 목록 조회 (페이지네이션, 검색, 필터링 지원)
 */
export async function getRegistrationList(options: RegistrationListOptions = {}): Promise<RegistrationListResponse> {
  try {
    const {
      page = 1,
      pageSize = 20,
      search = '',
      genderFilter = '',
      ageGroupFilter = '',
      regionFilter = '',
      entryStatusFilter = 'all',
      exhibitionTypeFilter = 'all',
      sortBy = 'created_at',
      sortOrder = 'desc'
    } = options;

    console.log('🔍 [getRegistrationList] 조회 옵션:', options);

    // 기본 쿼리 생성
    let query = supabaseClient.from('registrations').select('*', { count: 'exact' });

    // 검색 필터 (이름 또는 전화번호)
    if (search) {
      query = query.or(`name.ilike.%${search}%,phone.ilike.%${search}%`);
    }

    // 성별 필터
    if (genderFilter) {
      query = query.eq('gender', genderFilter);
    }

    // 연령대 필터
    if (ageGroupFilter) {
      query = query.eq('age_group', ageGroupFilter);
    }

    // 지역 필터
    if (regionFilter) {
      query = query.eq('region', regionFilter);
    }

    // 입장 상태 필터
    if (entryStatusFilter === 'entered') {
      query = query.eq('is_entered', true);
    } else if (entryStatusFilter === 'pending') {
      query = query.eq('is_entered', false);
    }

    // 전시회 타입 필터 (exhibition_type 컬럼 기반)
    if (exhibitionTypeFilter !== 'all') {
      console.log('🎯 [getRegistrationList] 전시회 필터 적용:', exhibitionTypeFilter);
      query = query.eq('exhibition_type', exhibitionTypeFilter);
    }

    // 정렬
    query = query.order(sortBy, { ascending: sortOrder === 'asc' });

    // 페이지네이션
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;
    query = query.range(from, to);

    const { data, error, count } = await query;

    if (error) {
      throw new Error(`등록자 목록 조회 실패: ${error.message}`);
    }

    const total = count || 0;
    const totalPages = Math.ceil(total / pageSize);

    const result = {
      data: data || [],
      total,
      page,
      pageSize,
      totalPages
    };

    console.log('✅ [getRegistrationList] 조회 완료:', {
      total: result.total,
      currentPage: page,
      exhibitionFilter: exhibitionTypeFilter
    });

    return result;
  } catch (error) {
    console.error('❌ [getRegistrationList] 등록자 목록 조회 에러:', error);
    throw error;
  }
}

/**
 * 등록자 입장 상태 변경
 */
export async function updateEntryStatus(id: string, isEntered: boolean): Promise<RegistrationData> {
  try {
    const { data, error } = await supabaseClient
      .from('registrations')
      .update({ 
        is_entered: isEntered,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      throw new Error(`입장 상태 변경 실패: ${error.message}`);
    }

    return data;
  } catch (error) {
    console.error('입장 상태 변경 에러:', error);
    throw error;
  }
}

/**
 * 등록자 정보 삭제
 */
export async function deleteRegistration(id: string): Promise<void> {
  try {
    const { error } = await supabaseClient
      .from('registrations')
      .delete()
      .eq('id', id);

    if (error) {
      throw new Error(`등록자 정보 삭제 실패: ${error.message}`);
    }
  } catch (error) {
    console.error('등록자 정보 삭제 에러:', error);
    throw error;
  }
}

/**
 * 등록자 목록 CSV 내보내기용 전체 데이터 조회
 */
export async function getRegistrationsForExport(): Promise<RegistrationData[]> {
  try {
    const { data, error } = await supabaseClient
      .from('registrations')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      throw new Error(`내보내기용 데이터 조회 실패: ${error.message}`);
    }

    return data || [];
  } catch (error) {
    console.error('내보내기용 데이터 조회 에러:', error);
    throw error;
  }
} 